import React, { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle2, Circle, Calendar, TrendingUp, Zap } from 'lucide-react';

const SEODashboard = () => {
  const [expandedSections, setExpandedSections] = useState({});
  const [completedTasks, setCompletedTasks] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const toggleTask = (taskId) => {
    setCompletedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  const recommendations = [
    {
      id: 'quick-wins',
      title: 'Quick Wins',
      subtitle: 'Implement Today (1-2 Hours)',
      priority: 'high',
      icon: Zap,
      color: 'orange',
      tasks: [
        { id: 'qw1', title: 'Add FAQ Schema to Blog Posts', description: 'Extract Q&A from each post and add FAQPage structured data' },
        { id: 'qw2', title: 'Optimize GMB Photos', description: 'Upload 20+ photos with descriptions and keywords' },
        { id: 'qw3', title: 'Create GMB Posts', description: 'Write 3 posts with offers linking to blog posts' },
        { id: 'qw4', title: 'Add Live Chat', description: 'Install Tawk.to (free) with automated greeting' },
        { id: 'qw5', title: 'Submit to Directories', description: 'Yelp, Yellow Pages, Facebook, Apple Maps, Bing Places' }
      ]
    },
    {
      id: 'ai-search',
      title: 'AI Search Optimization',
      subtitle: 'ChatGPT, Perplexity, Gemini, Claude',
      priority: 'high',
      icon: TrendingUp,
      color: 'blue',
      tasks: [
        { id: 'ai1', title: 'Add FAQ Schema Markup', description: 'AI engines prioritize FAQ structured data' },
        { id: 'ai2', title: 'Create "People Also Ask" Sections', description: 'Research PAA boxes and add exact question phrasing' },
        { id: 'ai3', title: 'Add Statistical Data', description: 'Include specific numbers: "150+ customers", "5.0 stars"' },
        { id: 'ai4', title: 'Update Content Monthly', description: 'Refresh blog posts with new info and updated dates' },
        { id: 'ai5', title: 'Create Data Tables', description: 'Pricing comparisons, service duration charts' },
        { id: 'ai6', title: 'Add Trust Signals', description: 'Display certifications, insurance, licensing' }
      ]
    },
    {
      id: 'local-seo',
      title: 'Local SEO Dominance',
      subtitle: 'Google My Business & Citations',
      priority: 'high',
      icon: TrendingUp,
      color: 'green',
      tasks: [
        { id: 'local1', title: 'Complete GMB Optimization', description: 'Fill all sections, add services with descriptions' },
        { id: 'local2', title: 'Weekly GMB Posts', description: 'Post weekly updates with keywords and blog links' },
        { id: 'local3', title: 'Target 200+ Google Reviews', description: 'Respond to every review with keywords' },
        { id: 'local4', title: 'Build 20+ Local Citations', description: 'Submit NAP to directories (consistent everywhere)' },
        { id: 'local5', title: 'Create Location Pages', description: '/locations/elk-grove, /roseville, /folsom with maps' },
        { id: 'local6', title: 'Get Local Backlinks', description: 'Chamber of Commerce, local press, dealerships' }
      ]
    },
    {
      id: 'content',
      title: 'Content Marketing',
      subtitle: 'Video, Blogs, Case Studies',
      priority: 'medium',
      icon: Calendar,
      color: 'purple',
      tasks: [
        { id: 'content1', title: 'Create 5 YouTube Videos', description: 'Before/after, ceramic coating process, timelapse' },
        { id: 'content2', title: 'Seasonal Content', description: 'Spring/Summer/Fall/Winter car care guides' },
        { id: 'content3', title: 'Dedicated FAQ Page', description: '50+ questions with FAQPage schema' },
        { id: 'content4', title: 'Case Studies', description: 'Before/after transformations with customer testimonials' },
        { id: 'content5', title: 'Comparison Posts', description: 'Ceramic vs Wax, Mobile vs Shop, DIY vs Pro' }
      ]
    },
    {
      id: 'technical',
      title: 'Technical SEO',
      subtitle: 'Search Console, Sitemaps, Core Web Vitals',
      priority: 'medium',
      icon: CheckCircle2,
      color: 'indigo',
      tasks: [
        { id: 'tech1', title: 'Submit to Google Search Console', description: 'Add property, submit sitemap, fix crawl errors' },
        { id: 'tech2', title: 'Submit to Bing Webmaster Tools', description: 'Expand search engine coverage' },
        { id: 'tech3', title: 'Optimize Core Web Vitals', description: 'Target green scores on LCP, FID, CLS' },
        { id: 'tech4', title: 'Create XML Sitemap', description: 'Include all blog posts with priority/changefreq' },
        { id: 'tech5', title: 'Add Breadcrumbs with Schema', description: 'Home > Blog > [Post] with structured data' }
      ]
    },
    {
      id: 'paid',
      title: 'Paid Advertising',
      subtitle: 'Google Ads, Local Services, Social',
      priority: 'low',
      icon: TrendingUp,
      color: 'pink',
      tasks: [
        { id: 'paid1', title: 'Launch Google Search Ads', description: 'Budget $30-50/day, high-intent keywords' },
        { id: 'paid2', title: 'Google Local Services Ads', description: 'Pay per lead, get "Google Screened" badge' },
        { id: 'paid3', title: 'Google Maps Ads', description: 'Appear prominent in Maps searches' },
        { id: 'paid4', title: 'Facebook/Instagram Ads', description: 'Target Elk Grove, Roseville, Folsom, $20-30/day' },
        { id: 'paid5', title: 'Nextdoor Sponsored Posts', description: 'Hyper-local neighborhood targeting' }
      ]
    }
  ];

  const priorityColors = {
    high: 'border-orange-500 bg-orange-50',
    medium: 'border-blue-500 bg-blue-50',
    low: 'border-gray-400 bg-gray-50'
  };

  const iconColors = {
    orange: 'text-orange-500',
    blue: 'text-blue-500',
    green: 'text-green-500',
    purple: 'text-purple-500',
    indigo: 'text-indigo-500',
    pink: 'text-pink-500'
  };

  const completedCount = Object.values(completedTasks).filter(Boolean).length;
  const totalTasks = recommendations.reduce((acc, section) => acc + section.tasks.length, 0);
  const progressPercentage = Math.round((completedCount / totalTasks) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white py-20">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            SEO Strategy Dashboard
          </h1>
          <p className="text-lg text-gray-400 mb-8">
            Track your progress toward AI search dominance and local SEO mastery
          </p>
          
          {/* Progress Bar */}
          <div className="max-w-2xl mx-auto bg-gray-800 rounded-full p-2">
            <div className="relative">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">Progress</span>
                <span className="text-orange-500 font-bold">{completedCount} of {totalTasks} tasks</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-4">
                <div 
                  className="bg-gradient-to-r from-orange-500 to-orange-600 h-4 rounded-full transition-all duration-500"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <div className="text-center mt-2 text-2xl font-bold text-orange-500">
                {progressPercentage}%
              </div>
            </div>
          </div>
        </div>

        {/* Recommendations Grid */}
        <div className="space-y-6">
          {recommendations.map((section) => {
            const Icon = section.icon;
            const sectionCompletedCount = section.tasks.filter(task => completedTasks[task.id]).length;
            const sectionProgress = Math.round((sectionCompletedCount / section.tasks.length) * 100);
            
            return (
              <div 
                key={section.id}
                className={`border-2 rounded-lg overflow-hidden ${priorityColors[section.priority]} bg-opacity-10 backdrop-blur-sm`}
              >
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full p-6 flex items-center justify-between hover:bg-white hover:bg-opacity-5 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <Icon className={`w-8 h-8 ${iconColors[section.color]}`} />
                    <div className="text-left">
                      <h2 className="text-2xl font-bold">{section.title}</h2>
                      <p className="text-gray-400 text-sm">{section.subtitle}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm text-gray-400">
                        {sectionCompletedCount}/{section.tasks.length} completed
                      </div>
                      <div className="text-lg font-bold text-orange-500">
                        {sectionProgress}%
                      </div>
                    </div>
                    {expandedSections[section.id] ? (
                      <ChevronUp className="w-6 h-6" />
                    ) : (
                      <ChevronDown className="w-6 h-6" />
                    )}
                  </div>
                </button>

                {/* Tasks List */}
                {expandedSections[section.id] && (
                  <div className="border-t border-gray-700 bg-black bg-opacity-30">
                    <div className="p-6 space-y-4">
                      {section.tasks.map((task, index) => (
                        <div
                          key={task.id}
                          className="flex items-start gap-4 p-4 rounded-lg bg-gray-900 hover:bg-gray-800 transition-all cursor-pointer"
                          onClick={() => toggleTask(task.id)}
                        >
                          <button
                            className="flex-shrink-0 mt-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTask(task.id);
                            }}
                          >
                            {completedTasks[task.id] ? (
                              <CheckCircle2 className="w-6 h-6 text-green-500" />
                            ) : (
                              <Circle className="w-6 h-6 text-gray-500" />
                            )}
                          </button>
                          <div className="flex-1">
                            <h3 className={`text-lg font-semibold mb-1 ${completedTasks[task.id] ? 'line-through text-gray-500' : ''}`}>
                              {task.title}
                            </h3>
                            <p className="text-gray-400 text-sm">
                              {task.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer CTA */}
        <div className="mt-12 text-center p-8 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg">
          <h3 className="text-2xl font-bold mb-2">Ready to Dominate Local Search?</h3>
          <p className="text-white text-opacity-90 mb-4">
            Full strategy document available at <code className="bg-black bg-opacity-30 px-2 py-1 rounded">/app/SEO_AEO_GEO_Strategy.md</code>
          </p>
          <a 
            href="tel:9162993189"
            className="inline-block bg-white text-orange-600 font-bold px-8 py-3 rounded-full hover:bg-gray-100 transition-all"
          >
            Call (916) 299-3189
          </a>
        </div>
      </div>
    </div>
  );
};

export default SEODashboard;
